/**
 * Real-time user state sync via Hyperliquid WebSocket.
 *
 * This is what keeps filled limit orders from lingering in the UI. Without it,
 * `account` / `openOrders` rely on 30s polling, so a fill isn't reflected until
 * the next poll (or much longer when the tab is backgrounded).
 *
 * Two subscriptions, both scoped to the connected wallet:
 *   - `orderUpdates` — status changes (open, filled, canceled, rejected, …).
 *                      Patches the `openOrders` cache via
 *                      `upsertOrdersFromWs` / `removeOrdersFromCache`.
 *   - `userFills`    — new fills. Invalidates `account` (positions/margin),
 *                      prepends into the `fills` infinite-query (dedup by tid),
 *                      invalidates `orderHistory`. Snapshot events: the first
 *                      per address (initial subscribe) is skipped, any later
 *                      one means the SDK reconnected — fills may have been
 *                      missed, so it triggers a delayed coalesced
 *                      fills + orderHistory refetch.
 *
 * The `onBeforeSubscribe` backstop refetches REST once whenever we
 * (re-)subscribe, closing any gap if events were missed while disconnected.
 * See `../features/trade/ARCHITECTURE.md` for the end-to-end picture.
 */
import type {
	OrderUpdatesEvent,
	UserFillsEvent,
} from "@nktkas/hyperliquid/api/subscription";
import { useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { useHyperliquidSubscription } from "#/features/trade/hooks/use-hyperliquid-subscription";
import { stripDexPrefix } from "#/features/trade/lib/hyperliquid";
import {
	invalidateAccountState,
	invalidateOpenOrders,
	scheduleAccountStateRefetch,
	scheduleFillHistoryRefetch,
} from "./invalidation-helpers";
import {
	removeOrdersFromCache,
	upsertOrdersFromWs,
	type WsOrderPatch,
} from "./optimistic-updates";
import { queryKeys } from "./query-keys";
import type { UserFill } from "./use-hyperliquid-account";
import { useWallet } from "./use-wallet";

type OrderStatus = OrderUpdatesEvent[number]["status"];

const TERMINAL_STATUSES: ReadonlySet<OrderStatus> = new Set<OrderStatus>([
	"filled",
	"canceled",
	"rejected",
	"marginCanceled",
	"vaultWithdrawalCanceled",
	"openInterestCapCanceled",
	"selfTradeCanceled",
	"reduceOnlyCanceled",
	"siblingFilledCanceled",
	"delistedCanceled",
	"liquidatedCanceled",
	"scheduledCancel",
	"tickRejected",
	"minTradeNtlRejected",
	"perpMarginRejected",
	"reduceOnlyRejected",
	"badAloPxRejected",
	"iocCancelRejected",
	"badTriggerPxRejected",
	"marketOrderNoLiquidityRejected",
	"positionIncreaseAtOpenInterestCapRejected",
	"positionFlipAtOpenInterestCapRejected",
	"tooAggressiveAtOpenInterestCapRejected",
	"openInterestIncreaseRejected",
	"insufficientSpotBalanceRejected",
	"oracleRejected",
	"perpMaxPositionRejected",
]);

const OPEN_STATUSES: ReadonlySet<OrderStatus> = new Set<OrderStatus>([
	"open",
	"triggered",
]);

type FillsInfinitePages = { pages: UserFill[][]; pageParams: unknown[] };

/**
 * Subscribes to Hyperliquid user event streams while a wallet is connected and
 * keeps the TanStack Query cache in sync in real time.
 *
 * Without this, `openOrders` / `account` rely on 30s polling, so a filled limit
 * order lingers as a ghost in the UI until the next poll (or much longer when
 * the tab is backgrounded), leading to failed cancel attempts on already-filled
 * orders.
 *
 * Mounted by `components/hyperliquid/hyper-user-events-sync.tsx`; while logged
 * out `useWallet()` returns `address: null` and the subscription stays disabled.
 */
export function useHyperUserEvents() {
	const { address } = useWallet();
	const queryClient = useQueryClient();
	// Which address has already received its initial `userFills` snapshot.
	// Deliberately survives SDK-level reconnects (they don't re-run the
	// subscription effect) and resets only when the address changes.
	const fillsSnapshotSeenFor = useRef<string | null>(null);

	const backstopRefetch = () => {
		if (!address) return;
		void invalidateAccountState(queryClient, address);
		void invalidateOpenOrders(queryClient, address);
	};

	useHyperliquidSubscription({
		enabled: address !== null,
		deps: [address],
		onBeforeSubscribe: backstopRefetch,
		subscribe: (client) =>
			client.orderUpdates({ user: address as `0x${string}` }, (events) => {
				if (!address || events.length === 0) return;

				const toRemove: { coin: string; oid: number }[] = [];
				const toUpsert: WsOrderPatch[] = [];
				let hadFillOrCancel = false;

				for (const { order, status } of events) {
					const coin = stripDexPrefix(order.coin);
					if (TERMINAL_STATUSES.has(status)) {
						toRemove.push({ coin, oid: order.oid });
						if (status === "filled" || status === "canceled") {
							hadFillOrCancel = true;
						}
						continue;
					}
					if (OPEN_STATUSES.has(status)) {
						toUpsert.push({
							coin,
							side: order.side,
							limitPx: order.limitPx,
							sz: order.sz,
							origSz: order.origSz,
							oid: order.oid,
							timestamp: order.timestamp,
							reduceOnly: order.reduceOnly,
						});
					}
				}

				if (toRemove.length > 0) {
					removeOrdersFromCache(queryClient, address, toRemove);
				}

				if (toUpsert.length > 0) {
					const outcomes = upsertOrdersFromWs(queryClient, address, toUpsert);
					if (outcomes.some((o) => o.kind === "unknown")) {
						void invalidateOpenOrders(queryClient, address);
					}
				}

				if (hadFillOrCancel) {
					// Delayed backstop, NOT an immediate invalidate: useHyperMainSync /
					// useHyperXyzSync already push the fresh position over WS. An
					// immediate REST refetch here would resolve with HL's stale
					// post-fill snapshot and clobber that fresh value (off-by-one).
					scheduleAccountStateRefetch(queryClient, address);
				}
			}),
	});

	useHyperliquidSubscription({
		enabled: address !== null,
		deps: [address],
		subscribe: (client) =>
			client.userFills(
				{ user: address as `0x${string}` },
				(event: UserFillsEvent) => {
					if (!address) return;
					if (event.isSnapshot) {
						// First snapshot per address = initial subscribe; the fills REST
						// query already covers it, and reacting here would double-fetch
						// on every page load. Any later snapshot means the SDK silently
						// reconnected and resubscribed — fills that landed while the
						// socket was down exist ONLY in this snapshot, but it carries up
						// to ~2000 historical fills so it can't be safely prepended.
						// Recover via a delayed, coalesced REST refetch instead.
						// A same-address wallet re-connect (effect-level resubscribe) is
						// intentionally NOT skipped — it refetches for the same reason.
						if (fillsSnapshotSeenFor.current !== address) {
							fillsSnapshotSeenFor.current = address;
							return;
						}
						scheduleFillHistoryRefetch(queryClient, address);
						return;
					}
					const fills = event.fills;
					if (!fills || fills.length === 0) return;

					// Delayed backstop (see orderUpdates above): the authoritative
					// position update arrives over WS; the REST refetch is only a
					// safety net and must not race-clobber it with a stale snapshot.
					scheduleAccountStateRefetch(queryClient, address);
					void queryClient.invalidateQueries({
						queryKey: queryKeys.hyper.orderHistory(address),
					});

					const fillsKey = queryKeys.hyper.fills(address);
					queryClient.setQueryData<FillsInfinitePages>(fillsKey, (old) => {
						if (!old || old.pages.length === 0) return old;
						const flat = old.pages.flat();
						const seen = new Set(flat.map((f) => f.tid));
						const fresh = fills
							.filter((f) => !seen.has(f.tid))
							.map((f) => ({ ...f, coin: stripDexPrefix(f.coin) }));
						if (fresh.length === 0) return old;
						const [firstPage, ...rest] = old.pages;
						return {
							...old,
							pages: [[...fresh, ...firstPage], ...rest],
						};
					});
				},
			),
	});
}
